import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import onsiteproLogo from "../../assets/logo2 2.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function parseStoredDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const day = parseInt(dmy[1], 10);
    const month = parseInt(dmy[2], 10) - 1;
    const year = parseInt(dmy[3], 10);
    const d = new Date(year, month, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const dmyHm = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/);
  if (dmyHm) {
    const day = parseInt(dmyHm[1], 10);
    const month = parseInt(dmyHm[2], 10) - 1;
    const year = parseInt(dmyHm[3], 10);
    const hour = parseInt(dmyHm[4], 10);
    const minute = parseInt(dmyHm[5], 10);
    const d = new Date(year, month, day, hour, minute);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const year = parseInt(ymd[1], 10);
    const month = parseInt(ymd[2], 10) - 1;
    const day = parseInt(ymd[3], 10);
    const d = new Date(year, month, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateToYmd(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function dateToDmyHm(d) {
  if (!d) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hour = String(d.getHours()).padStart(2, "0");
  const minute = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hour}:${minute}`;
}

const INJURY_TYPE_OPTIONS = [
  "not yet known", "irritation", "burn / scald", "foreign body", "unconscious",
  "strain / sprain", "amputation", "fracture", "Shock", "trap / crush",
  "penetration", "asphyxiation", "bruise", "Cut", "graze",
  "inhalation", "ingestion", "Other"
];

const BODY_TYPE_OPTIONS = [
  "abdomen", "groin", "head", "nose", "mouth", "neck",
  "back", "buttocks", "face", "jaw", "throat", "chest",
  "L finger", "R finger", "L ear", "R ear",
  "L leg", "R leg", "L shoulder", "R shoulder",
  "L knee", "R knee", "L arm", "R arm",
  "L ankle", "R ankle", "L elbow", "R elbow",
  "L foot", "R foot", "L wrist", "R wrist",
  "L eye", "R eye", "L hand", "R hand"
];

function toDateInputValue(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = parseStoredDate(raw);
  if (!parsed) return "";
  return dateToYmd(parsed);
}

function normalizeIncidentPhotos(value) {
  if (Array.isArray(value)) return value.map((x) => String(x || "").trim()).filter(Boolean);
  return [];
}

function normalizeSf003cPhotosTaken(record) {
  const raw = record?.photosTaken;
  const s = String(raw ?? "").trim();
  const lower = s.toLowerCase();
  if (lower === "yes" || lower === "y" || s === "YES") return "Yes";
  if (lower === "no" || lower === "n" || s === "NO") return "No";
  if (normalizeIncidentPhotos(record?.incidentPhotos).length > 0) return "Yes";
  return "";
}

function toStringArray(value) {
  if (Array.isArray(value)) return value.map((x) => String(x || "").trim()).filter(Boolean);
  if (typeof value === "string" && value.trim()) {
    const raw = value.trim();
    if (raw.startsWith("[") || raw.startsWith("{")) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map((x) => String(x || "").trim()).filter(Boolean);
      } catch (_e) {}
    }
    return raw
      .split(/[,\n]/)
      .map((x) => x.trim())
      .filter(Boolean);
  }
  return [];
}

function parseObjectLike(value) {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string") {
    const raw = value.trim();
    if (raw.startsWith("{")) {
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
      } catch (_e) {
        return {};
      }
    }
  }
  return {};
}

function parseWitnessTuple(value) {
  if (typeof value !== "string") return {};
  const raw = value.trim();
  if (!raw) return {};
  const parts = raw
    .split(/[|,]/)
    .map((x) => x.trim())
    .filter(Boolean);
  if (parts.length < 2) return {};
  return {
    firstNameSurname: parts[0] || "",
    titlePosition: parts[1] || "",
    telephoneNumber: parts[2] || "",
  };
}

function normalizeChoicesByOptions(options, value) {
  const input = toStringArray(value);
  if (!input.length) return [];
  const normalize = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "");
  const byNorm = new Map(options.map((o) => [normalize(o), o]));
  const out = [];
  for (const token of input) {
    const direct = byNorm.get(normalize(token));
    if (direct) {
      if (!out.includes(direct)) out.push(direct);
      continue;
    }
    const n = normalize(token);
    const fuzzy = options.find((o) => normalize(o).includes(n) || n.includes(normalize(o)));
    if (fuzzy && !out.includes(fuzzy)) out.push(fuzzy);
  }
  return out;
}

function normalizeInjuredOutcome(record) {
  const outcome = String(record?.injuredPersonOutcome || "").trim();
  if (outcome) return outcome;
  if (String(record?.injuredReturnedToWork || "").trim()) return "Returned to work";
  if (String(record?.injuredSentHome || "").trim()) return "Sent home";
  if (String(record?.injuredSentToHospital || "").trim()) return "Sent to hospital";
  return "";
}

function normalizeOtherCondition(value, otherValue) {
  const v = String(value || "").trim();
  if (!v) return "";
  if (v === "Other") return "Other (describe)";
  if (v === "Other (describe)") return "Other (describe)";
  if (otherValue && !["Fine", "Raining", "Snow/Ice", "Windy", "Sunny", "Cloudy", "Dark", "Indoors"].includes(v)) {
    return "Other (describe)";
  }
  return v;
}

function mapRecordToForm(record) {
  const person1 = record.person1 || {};
  const person2 = record.person2 || {};
  const witnessObj = parseObjectLike(record.detailsOfWitnesses);
  const witnessFallback = parseObjectLike(record.witnessDetails);
  const witnessTuple = parseWitnessTuple(record.witnessDetails);
  const witnessNameFromParts =
    [witnessObj.firstName || witnessFallback.firstName, witnessObj.surname || witnessFallback.surname]
      .filter(Boolean)
      .join(" ")
      .trim();
  const witnessTitleFromParts =
    [witnessObj.title || witnessFallback.title, witnessObj.position || witnessFallback.position]
      .filter(Boolean)
      .join("/")
      .trim();
  return {
    ...record,
    incidentDate: toDateInputValue(record.incidentDate),
    notification: {
      clientTimeDate: record.notification?.clientTimeDate || record.notificationClient || "",
      projectLineManagerTimeDate: record.notification?.projectLineManagerTimeDate || record.notificationProjectLineManager || "",
      hsqeManagerTimeDate: record.notification?.hsqeManagerTimeDate || record.notificationHsqueManager || ""
    },
    detailsOfInjuredAffectedPerson: {
      surname: person1.surname || record.injuredAffectedPersonDetails || "",
      firstName: person1.firstName || record.injuredFirstName || record.reviewFirstNameSurname || "",
      employmentType: person1.employmentType || record.injuredEmploymentType || "",
      employerName: person1.employerName || record.injuredEmployerName || "",
      managerOrSupervisor: person1.managerOrSupervisor || record.injuredManagerOrSupervisor || "",
      telephoneNumber: person1.telephoneNumber || record.injuredTelephoneNumber || "",
      projectOrContractingCompany: person1.projectOrContractingCompany || record.injuredProjectOrContractingCompany || "",
      person2: {
        surname: person2.surname || record.injured2Surname || "",
        firstName: person2.firstName || record.injured2FirstName || "",
        employmentType: person2.employmentType || record.injured2EmploymentType || "",
        employerName: person2.employerName || record.injured2EmployerName || "",
        managerOrSupervisor: person2.managerOrSupervisor || record.injured2ManagerOrSupervisor || "",
        telephoneNumber: person2.telephoneNumber || record.injured2TelephoneNumber || "",
        projectOrContractingCompany: person2.projectOrContractingCompany || record.injured2ProjectOrContractingCompany || ""
      }
    },
    injuryTypeOther: record.injuryTypeOther || "",
    injuryType: normalizeChoicesByOptions(
      INJURY_TYPE_OPTIONS,
      record.injuryType?.length ? record.injuryType : record.detailsOfInjury
    ),
    injuryBodyType: normalizeChoicesByOptions(
      BODY_TYPE_OPTIONS,
      record.injuryBodyType?.length ? record.injuryBodyType : record.bodyPartsAffected
    ),
    injuredPersonOutcome: normalizeInjuredOutcome(record),
    hospitalDetailsName: record.hospitalDetails?.name || record.hospitalDetailsName || "",
    hospitalDetailsPhone: record.hospitalDetails?.phoneNumber || record.hospitalDetailsPhone || "",
    reviewFirstNameSurname:
      witnessObj.firstNameSurname ||
      witnessFallback.firstNameSurname ||
      witnessTuple.firstNameSurname ||
      witnessNameFromParts ||
      record.firstNameSurname ||
      record.reviewFirstNameSurname ||
      (typeof record.witnessDetails === "string" ? record.witnessDetails : "") ||
      "",
    reviewTitlePosition:
      witnessObj.titlePosition ||
      witnessFallback.titlePosition ||
      witnessTuple.titlePosition ||
      witnessTitleFromParts ||
      record.titlePosition ||
      record.reviewTitlePosition ||
      "",
    reviewTelephoneNumber:
      witnessObj.telephoneNumber ||
      witnessObj.telephone ||
      witnessObj.phoneNumber ||
      witnessObj.phone ||
      witnessFallback.telephoneNumber ||
      witnessFallback.telephone ||
      witnessFallback.phoneNumber ||
      witnessFallback.phone ||
      witnessTuple.telephoneNumber ||
      record.telephoneNumber ||
      record.reviewTelephoneNumber ||
      "",
    forCauseDrugAlcoholRequested:
      record.reportingAndCompletion?.forCauseDrugAlcoholRequested || record.forCauseDrugAlcoholRequested || "",
    clientNotified: record.reportingAndCompletion?.clientNotified || record.clientNotified || "",
    reportCompletedName: record.reportingAndCompletion?.reportCompletedName || record.reportCompletedName || "",
    reportCompletedTelephone:
      record.reportingAndCompletion?.reportCompletedTelephone || record.reportCompletedTelephone || "",
    weatherConditions: normalizeOtherCondition(record.weatherConditions, record.weatherOtherDescribe),
    lightingConditions: normalizeOtherCondition(record.lightingConditions, record.lightingOtherDescribe),
    photosTaken: normalizeSf003cPhotosTaken(record) || "No",
    incidentPhotos: normalizeIncidentPhotos(record.incidentPhotos)
  };
}

function mapFormToPayload(form) {
  const payload = { ...form };
  payload.notificationClient = form.notification?.clientTimeDate || "";
  payload.notificationProjectLineManager = form.notification?.projectLineManagerTimeDate || "";
  payload.notificationHsqueManager = form.notification?.hsqeManagerTimeDate || "";
  payload.person1 = {
    surname: form.detailsOfInjuredAffectedPerson?.surname || "",
    firstName: form.detailsOfInjuredAffectedPerson?.firstName || "",
    employmentType: form.detailsOfInjuredAffectedPerson?.employmentType || "",
    employerName: form.detailsOfInjuredAffectedPerson?.employerName || "",
    managerOrSupervisor: form.detailsOfInjuredAffectedPerson?.managerOrSupervisor || "",
    telephoneNumber: form.detailsOfInjuredAffectedPerson?.telephoneNumber || "",
    projectOrContractingCompany: form.detailsOfInjuredAffectedPerson?.projectOrContractingCompany || ""
  };
  payload.person2 = {
    surname: form.detailsOfInjuredAffectedPerson?.person2?.surname || "",
    firstName: form.detailsOfInjuredAffectedPerson?.person2?.firstName || "",
    employmentType: form.detailsOfInjuredAffectedPerson?.person2?.employmentType || "",
    employerName: form.detailsOfInjuredAffectedPerson?.person2?.employerName || "",
    managerOrSupervisor: form.detailsOfInjuredAffectedPerson?.person2?.managerOrSupervisor || "",
    telephoneNumber: form.detailsOfInjuredAffectedPerson?.person2?.telephoneNumber || "",
    projectOrContractingCompany: form.detailsOfInjuredAffectedPerson?.person2?.projectOrContractingCompany || ""
  };
  payload.injuryType = Array.isArray(form.injuryType) ? form.injuryType : [];
  payload.injuryTypeOther = form.injuryTypeOther || "";
  payload.injuryBodyType = Array.isArray(form.injuryBodyType) ? form.injuryBodyType : [];
  {
    const pt = String(form.photosTaken || "").trim().toLowerCase();
    payload.incidentPhotos = pt === "no" ? [] : normalizeIncidentPhotos(form.incidentPhotos);
  }
  payload.injuredPersonOutcome = form.injuredPersonOutcome || "";
  payload.injuryDetails = payload.injuryType.includes("Other")
    ? `Other: ${form.injuryTypeOther || ""}`.trim()
    : payload.injuryType.join(", ");
  payload.injuredReturnedToWork = "";
  payload.injuredSentHome = "";
  payload.injuredSentToHospital = "";
  payload.hospitalDetails = {
    name: form.hospitalDetailsName || "",
    phoneNumber: form.hospitalDetailsPhone || ""
  };
  payload.detailsOfWitnesses = {
    firstNameSurname: form.reviewFirstNameSurname || "",
    titlePosition: form.reviewTitlePosition || "",
    telephoneNumber: form.reviewTelephoneNumber || ""
  };
  payload.reportingAndCompletion = {
    forCauseDrugAlcoholRequested: form.forCauseDrugAlcoholRequested || "",
    clientNotified: form.clientNotified || "",
    reportCompletedName: form.reportCompletedName || "",
    reportCompletedTelephone: form.reportCompletedTelephone || ""
  };
  delete payload.notification;
  delete payload.detailsOfInjuredAffectedPerson;
  delete payload.hospitalDetailsName;
  delete payload.hospitalDetailsPhone;
  delete payload.witnessDetails;
  delete payload.reviewFirstNameSurname;
  delete payload.reviewTitlePosition;
  delete payload.reviewTelephoneNumber;
  delete payload.forCauseDrugAlcoholRequested;
  delete payload.clientNotified;
  delete payload.reportCompletedBy;
  delete payload.reportCompletedName;
  delete payload.reportCompletedTelephone;
  return payload;
}

export default function IncidentReportSf003CPage() {
  const canEditDelete = canEditDeleteRecords();
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [incidentPhotoInputKey, setIncidentPhotoInputKey] = useState(0);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState("phase");
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState("15");
  const [sharePointNetwork, setSharePointNetwork] = useState("");
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);

  useEffect(() => {
    fetchRecords();
    fetchAppUsers();
  }, []);

  async function fetchRecords() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/incident-report-sf003c", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRecords(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const getUserName = (record) => {
    const uid = record?.userId;
    const u = appUsers.find((x) => x._id === uid);
    if (u) {
      const full = `${u.firstName || ""} ${u.lastName || ""}`.trim();
      return full || "—";
    }
    const named = `${record?.addedByFirstName || ""} ${record?.addedByLastName || ""}`.trim();
    return named || "—";
  };

  const filtered = records.filter((r) => {
    const fields = [r.location, r.incidentDate, r.incidentTime, r.projectSiteManagersName, r.supervisorsName].map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page - 1) * pageSize, page * pageSize);

  const handleEdit = (r) => {
    setEditForm({ ...mapRecordToForm(r), formUuid: ensureFormUuid(r?.formUuid) });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const { userId, ...formWithoutUser } = editForm;
      const payload = { ...mapFormToPayload(formWithoutUser), formUuid: ensureFormUuid(editForm.formUuid) };
      const res = await fetch(getBaseUrl() + "/api/incident-report-sf003c/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchRecords();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("Record updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) {
      toast.error("Invalid record id");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/incident-report-sf003c/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(records.filter((f) => f._id !== deleteId));
        toast.success("Record deleted!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleIncidentPhotosChange = async (e) => {
    const files = Array.from(e.target.files || []);
    setIncidentPhotoInputKey((k) => k + 1);
    if (!editForm) return;
    const existing = normalizeIncidentPhotos(editForm.incidentPhotos);
    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select image files only.");
        continue;
      }
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error("read"));
          reader.readAsDataURL(file);
        });
        if (typeof dataUrl === "string") existing.push(dataUrl);
      } catch {
        toast.error("Failed to read an image file.");
      }
    }
    setEditForm({ ...editForm, incidentPhotos: existing });
  };

  const removeIncidentPhotoAt = (index) => {
    if (!editForm) return;
    const arr = normalizeIncidentPhotos(editForm.incidentPhotos);
    arr.splice(index, 1);
    setEditForm({ ...editForm, incidentPhotos: arr });
  };

  const handleSharePointUpload = async () => {
    const locationForUpload = (sharePointUploadRecord?.location || "").trim();
    if (!locationForUpload) {
      toast.error("Location is missing for this record.");
      return;
    }
    if (sharePointPhaseType === "phase" && (!sharePointNetwork || sharePointNetwork.trim() === "")) {
      toast.error("Please select Area.");
      return;
    }
    const payloadPhaseType = sharePointPhaseType === "phase" ? "phase" : "non-phase";
    const payloadNetwork = sharePointPhaseType === "phase" ? sharePointNetwork : "";
    const payloadPhaseNumber = sharePointPhaseType === "phase" ? sharePointPhaseNumber : "";
    setShowSharePointUploadModal(false);
    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: locationForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType("phase");
        setSharePointPhaseNumber("15");
        setSharePointNetwork("");
      },
      onError: () => {
        setPendingDirectUpload(false);
      }
    });
  };

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint
      ? {
          phaseType: options.phaseType || "non-phase",
          phaseNumber: options.phaseNumber || "",
          network: options.network || "",
          siteName: options.siteName || "",
          onSuccess: options.onSuccess,
          onError: options.onError
        }
      : null;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const primaryColor = [139, 22, 52];
    const headerHeight = 30;

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, headerHeight, "F");
    try {
      const logoImg = new Image();
      await new Promise((resolve) => {
        logoImg.onload = resolve;
        logoImg.onerror = resolve;
        logoImg.src = onsiteproLogo;
      });
      doc.addImage(logoImg, "PNG", 10, 5, 65, 20);
    } catch {}

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("SF-003-C Incident Report", 90, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", 90, 19);

    let y = headerHeight + 8;
    const common = {
      styles: { font: "helvetica", fontSize: 9, cellPadding: 3, overflow: "linebreak", textColor: [0, 0, 0] },
      theme: "grid",
      headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], lineWidth: 0.2 },
      bodyStyles: { textColor: [0, 0, 0], lineWidth: 0.2 },
      margin: { left: 10, right: 10 }
    };
    const normalizeYesNo = (value) => String(value || "").trim().toLowerCase();
    const boolMark = (value) => {
      const v = normalizeYesNo(value);
      if (v === "yes" || v === "y") return "YES";
      if (v === "no" || v === "n") return "NO";
      if (value === true || value === "true") return "YES";
      if (value === false || value === "false") return "NO";
      const s = value != null ? String(value).trim() : "";
      return s !== "" ? s : "—";
    };
    const drawSection = (index, title) => {
      if (y > pageHeight - 24) {
        doc.addPage();
        y = 20;
      }
      doc.setFillColor(...primaryColor);
      doc.rect(10, y, pageWidth - 20, 8, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text(String(index), 14, y + 5.5);
      doc.text(title, 22, y + 5.5);
      y += 8;
    };

    drawSection(1, "Incident Details");
    autoTable(doc, {
      ...common,
      startY: y,
      body: [
        [{ content: "Location", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.location || "", { content: "Date", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.incidentDate || ""],
        [{ content: "Time", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.incidentTime || "", { content: "Project / Site Managers Name", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.projectSiteManagersName || ""],
        [{ content: "Supervisors Name", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, { content: record.supervisorsName || "", colSpan: 3 }],
        [{ content: "Was anyone injured?", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, boolMark(record.wasAnyoneInjured), { content: "Added By", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, getUserName(record)]
      ]
    });
    y = doc.lastAutoTable.finalY + 2;

    drawSection(2, "Conditions and Controls");
    autoTable(doc, {
      ...common,
      startY: y,
      body: [
        ...(record.weatherOtherDescribe
          ? [[
            { content: "Weather Conditions", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            record.weatherConditions || "",
            { content: "Other (describe)", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            record.weatherOtherDescribe || ""
          ]]
          : [[
            { content: "Weather Conditions", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.weatherConditions || "", colSpan: 3 }
          ]]),
        ...(record.lightingOtherDescribe
          ? [[
            { content: "Lighting Conditions", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            record.lightingConditions || "",
            { content: "Other (describe)", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            record.lightingOtherDescribe || ""
          ]]
          : [[
            { content: "Lighting Conditions", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.lightingConditions || "", colSpan: 3 }
          ]]),
        [{ content: "State PPE worn at time of Incident", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, { content: record.statePpeWornAtTimeOfIncident || "", colSpan: 3 }],
        [{ content: "Fatigue/FRI assessment", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, { content: record.fatigueFriAssessment || "", colSpan: 3 }],
        [{ content: "Photos taken", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, boolMark(record.photosTaken), { content: "For Cause D&A initiated", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, boolMark(record.forCauseDaTestingInitiated)]
      ]
    });
    y = doc.lastAutoTable.finalY + 2;

    const incidentPhotoList = Array.isArray(record.incidentPhotos) ? record.incidentPhotos.filter(Boolean) : [];
    const resolveIncidentPhotoSrc = (raw) => {
      const r = typeof raw === "string" ? raw.trim() : "";
      if (!r) return "";
      if (r.startsWith("data:image") || r.startsWith("http://") || r.startsWith("https://")) return r;
      return `data:image/jpeg;base64,${String(raw)}`;
    };
    const inferPdfImageFormat = (src) => {
      let imgFmt = "JPEG";
      const mimeMatch = src.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/i);
      if (mimeMatch) {
        const sub = mimeMatch[1].toLowerCase();
        if (sub === "png") imgFmt = "PNG";
        else if (sub === "jpeg" || sub === "jpg") imgFmt = "JPEG";
        else imgFmt = "PNG";
      }
      return imgFmt;
    };

    if (incidentPhotoList.length > 0) {
      const innerX = 14;
      const innerRight = pageWidth - 14;
      const usableW = innerRight - innerX;
      const gridGap = 6;
      const cellMaxW = (usableW - gridGap) / 2;
      const cellMaxH = 34;

      if (y > pageHeight - 48) {
        doc.addPage();
        y = 20;
      }
      doc.setFillColor(...primaryColor);
      doc.rect(10, y, pageWidth - 20, 8, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text(`Incident photographs (${incidentPhotoList.length})`, innerX, y + 5.5);
      y += 10;
      doc.setTextColor(0, 0, 0);

      const drawPhotoInCell = (raw, x0, yTop) =>
        new Promise((resolve) => {
          const src = resolveIncidentPhotoSrc(raw);
          if (!src) {
            resolve(yTop);
            return;
          }
          const imgFmt = inferPdfImageFormat(src);
          const im = new Image();
          im.onload = () => {
            try {
              const iw = im.naturalWidth || 1;
              const ih = im.naturalHeight || 1;
              let dw = cellMaxW;
              let dh = (ih / iw) * dw;
              if (dh > cellMaxH) {
                dh = cellMaxH;
                dw = (iw / ih) * dh;
              }
              doc.addImage(src, imgFmt, x0, yTop, dw, dh);
              resolve(yTop + dh);
            } catch (_e) {
              doc.setFont("helvetica", "normal");
              doc.setFontSize(8);
              doc.text("(Could not embed)", x0, yTop + 4);
              resolve(yTop + 8);
            }
          };
          im.onerror = () => {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.text("(Could not load)", x0, yTop + 4);
            resolve(yTop + 8);
          };
          im.src = src;
        });

      for (let pi = 0; pi < incidentPhotoList.length; pi += 2) {
        if (y + cellMaxH + 22 > pageHeight - 10) {
          doc.addPage();
          y = 20;
        }

        const xL = innerX;
        const xR = innerX + cellMaxW + gridGap;
        const yRowStart = y;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(7.5);
        doc.setTextColor(55, 55, 55);
        doc.text(`${pi + 1} / ${incidentPhotoList.length}`, xL, yRowStart);
        if (pi + 1 < incidentPhotoList.length) {
          doc.text(`${pi + 2} / ${incidentPhotoList.length}`, xR, yRowStart);
        }
        const yImg = yRowStart + 4;

        const bottomL = await drawPhotoInCell(incidentPhotoList[pi], xL, yImg);
        const bottomR =
          pi + 1 < incidentPhotoList.length
            ? await drawPhotoInCell(incidentPhotoList[pi + 1], xR, yImg)
            : yImg;

        y = Math.max(bottomL, bottomR) + 7;
        doc.setTextColor(0, 0, 0);
      }
      y += 2;
    }

    drawSection(3, "Notification and Environmental Details");
    const notifLabelW = 58;
    const notifValueW = pageWidth - 20 - notifLabelW;
    autoTable(doc, {
      ...common,
      startY: y,
      tableWidth: pageWidth - 20,
      columnStyles: {
        0: { cellWidth: notifLabelW, fillColor: [230, 230, 230], fontStyle: "bold", valign: "top" },
        1: { cellWidth: notifValueW, overflow: "linebreak", valign: "top" }
      },
      styles: {
        ...common.styles,
        cellPadding: { top: 4, right: 5, bottom: 4, left: 5 },
        fontSize: 9,
        overflow: "linebreak"
      },
      body: [
        [
          { content: "Notification Client (Time/Date)" },
          { content: String(record.notificationClient || "").trim() || "—" }
        ],
        [
          { content: "Notification Project / Line Manager (Time/Date)" },
          { content: String(record.notificationProjectLineManager || "").trim() || "—" }
        ],
        [
          { content: "Notification HSQE Manager (Time/Date)" },
          { content: String(record.notificationHsqueManager || "").trim() || "—" }
        ]
      ]
    });
    y = doc.lastAutoTable.finalY + 2;

    drawSection(4, "Incident Description");
    autoTable(doc, {
      ...common,
      startY: y,
      body: [
        [{ content: "Environmental Details", styles: { fillColor: [230, 230, 230], fontStyle: "bold" }, colSpan: 4 }],
        [{ content: "Spillage / Leakage", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, { content: record.environmentalSpillageLeakage || "", colSpan: 3 }],
        [{ content: "Material / Substance", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.environmentalMaterialSubstance || "", { content: "Quantity (litres)", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.environmentalQuantityLitres || ""],
        [{ content: "Other (Noise, Emission to air)", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, { content: record.environmentalOtherNoiseEmission || "", colSpan: 3 }],
        [{ content: "Location (ground, drain, watercourse)", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.environmentalLocation || "", { content: "Affected Protected Site(s)", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, boolMark(record.affectedProtectedSites)],
        [{ content: "", styles: { fillColor: [255, 255, 255] } }, "", { content: "Affected Protected Species", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, boolMark(record.affectedProtectedSpecies)]
      ]
    });
    y = doc.lastAutoTable.finalY + 2;

    drawSection(5, "Injured/Affected Persons and Injury");
    autoTable(doc, {
      ...common,
      startY: y,
      body: [
        [{ content: "Describe what happened", styles: { fillColor: [230, 230, 230], fontStyle: "bold" }, colSpan: 4 }],
        [{ content: record.descriptionWhatHappened || "—", colSpan: 4 }],
        [{ content: "Immediate cause", styles: { fillColor: [230, 230, 230], fontStyle: "bold" }, colSpan: 4 }],
        [{ content: record.immediateCause || "—", colSpan: 4 }],
        [{ content: "Details of how incident was managed/reported", styles: { fillColor: [230, 230, 230], fontStyle: "bold" }, colSpan: 4 }],
        [{ content: record.detailsManagedAndReported || "—", colSpan: 4 }],
        [{ content: "Remedial actions", styles: { fillColor: [230, 230, 230], fontStyle: "bold" }, colSpan: 4 }],
        [{ content: record.remedialActions || "—", colSpan: 4 }]
      ]
    });
    y = doc.lastAutoTable.finalY + 2;

    drawSection(6, "Review and Completion");
    autoTable(doc, {
      ...common,
      startY: y,
      body: [
        [{ content: "Details of injured/affected person", styles: { fillColor: [230, 230, 230], fontStyle: "bold" }, colSpan: 4 }],
        [{ content: "Person 1 Surname", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.injuredAffectedPersonDetails || "—", { content: "Person 1 First Name", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.injuredFirstName || "—" ],
        [{ content: "Person 1 Type", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.injuredEmploymentType || "—", { content: "Employer Name", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.injuredEmployerName || "—" ],
        [{ content: "Manager/Supervisor", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.injuredManagerOrSupervisor || "—", { content: "Telephone", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.injuredTelephoneNumber || "—" ],
        [{ content: "Project/Contracting Company", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, { content: record.injuredProjectOrContractingCompany || "—", colSpan: 3 }],
        [{ content: "Person 2 Surname", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.injured2Surname || "—", { content: "Person 2 First Name", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.injured2FirstName || "—" ],
        [{ content: "Person 2 Type", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.injured2EmploymentType || "—", { content: "Employer Name", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.injured2EmployerName || "—" ],
        [{ content: "Manager/Supervisor", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.injured2ManagerOrSupervisor || "—", { content: "Telephone", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.injured2TelephoneNumber || "—" ],
        [{ content: "Project/Contracting Company", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, { content: record.injured2ProjectOrContractingCompany || "—", colSpan: 3 }],
        [{ content: "Injury Type", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, toStringArray(record.injuryType).join(", ") || "—", { content: "Injury Type Other", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.injuryTypeOther || "—" ],
        [{ content: "Body Type", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, toStringArray(record.injuryBodyType).join(", ") || "—", { content: "Details of Injury", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.injuryDetails || "—" ],
        [{ content: "The injured person was", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, { content: record.injuredPersonOutcome || "—", colSpan: 3 }],
        [{ content: "Hospital Name / Phone", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, `${record.hospitalDetailsName || "—"} / ${record.hospitalDetailsPhone || "—"}`, { content: "For Cause Drug/Alcohol", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, boolMark(record.forCauseDrugAlcoholRequested)],
        [{ content: "Details of Witnesses", styles: { fillColor: [230, 230, 230], fontStyle: "bold" }, colSpan: 4 }],
        [{ content: record.witnessDetails || "—", colSpan: 4 }]
      ]
    });
    y = doc.lastAutoTable.finalY + 2;

    autoTable(doc, {
      ...common,
      startY: y,
      body: [
        [{ content: "Review", styles: { fillColor: [230, 230, 230], fontStyle: "bold" }, colSpan: 4 }],
        [{ content: "First name/Surname", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.reviewFirstNameSurname || "", { content: "Title/Position", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.reviewTitlePosition || ""],
        [{ content: "Telephone Number", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.reviewTelephoneNumber || "", { content: "For Cause Drug/Alcohol requested", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, boolMark(record.forCauseDrugAlcoholRequested)],
        [{ content: "Has client been notified?", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, boolMark(record.clientNotified), { content: "Report Completed By", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.reportCompletedBy || ""],
        [{ content: "Completed Name", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.reportCompletedName || "", { content: "Completed Telephone", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, record.reportCompletedTelephone || ""]
      ]
    });

    const safeLoc = String(record.location || "incident").replace(/[\\/:"*?<>|]+/g, "-");
    const safeDate = String(record.incidentDate || "date").replace(/[\\/:"*?<>|]+/g, "-");
    const fileName = shouldUploadToSharePoint
      ? buildSharePointFileName([addedByName(record), record.location, toDdMmYyyy(record.incidentDate)])
      : `SF003C_${safeLoc}_${safeDate}.pdf`;

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${record.location || ""}|${record.incidentDate || ""}`;
        setUploadingKey(key);
        const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        const dataUri = doc.output("datauristring");
        const uploadParams = {
          fileName,
          fileBase64: dataUri,
          phaseType: uploadConfig.phaseType,
          phaseNumber: uploadConfig.phaseNumber,
          network: uploadConfig.network,
          siteName: uploadConfig.siteName, uploadContext: "hseq-forms-tablets", uploadSubFolder: "SF-003-C Incident Report"
        };
        const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify(uploadParams)
        });
        if (res.status !== 204 && !res.ok) {
          let msg = "SharePoint upload failed";
          try {
            const out = await res.json();
            msg = out?.message || msg;
          } catch {}
          throw new Error(msg);
        }
        toast.success("PDF uploaded to SharePoint successfully!");
        if (uploadConfig.onSuccess) uploadConfig.onSuccess();
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
        if (uploadConfig.onError) uploadConfig.onError(err);
      } finally {
        setUploadingKey(null);
      }
    } else {
      doc.save(fileName);
      toast.success("SF-003-C Incident Report PDF generated successfully!");
    }
  };

  const inputStyle = { border: "1px solid #d1d5db", borderRadius: 6, fontSize: 12, padding: "7px 10px", minHeight: 34, boxShadow: "none", background: "#fff" };
  const dateInputStyle = { ...inputStyle, color: THEME, fontWeight: 600, border: `1px solid ${THEME}` };
  const tableStyle = { width: "100%", borderCollapse: "separate", borderSpacing: 0, marginBottom: 20, tableLayout: "fixed", background: "#fff", border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden" };
  const headerCell = { borderRight: "1px solid #cbd5e1", borderBottom: "1px solid #cbd5e1", background: "#efe2e7", fontWeight: 700, fontSize: 12, padding: "8px 10px", verticalAlign: "middle", color: THEME };
  const labelCell = { borderRight: "1px solid #cbd5e1", borderBottom: "1px solid #cbd5e1", background: "#f8f1f4", fontWeight: 700, fontSize: 12, padding: "8px 10px", verticalAlign: "middle", color: THEME };
  const bodyCell = { borderRight: "1px solid #cbd5e1", borderBottom: "1px solid #cbd5e1", background: "#fff", fontSize: 12, padding: 6, verticalAlign: "middle" };
  const plainCell = { borderRight: "1px solid #cbd5e1", borderBottom: "1px solid #cbd5e1", background: "#fff", fontSize: 12, padding: "8px 10px", verticalAlign: "middle", color: "#374151" };
  const radioWrap = { display: "flex", alignItems: "center", gap: 14, padding: "6px 8px", minHeight: 34, flexWrap: "wrap" };
  const themedRadio = { accentColor: THEME };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-003-C Incident Report</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
            <input type="text" placeholder="Type to Search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250 }} />
          </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Location</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Time</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Project/Site Manager</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}><i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />Loading records...</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>No records found.</td></tr>
              ) : (
                paged.map((r, idx) => {
                  const updatedDate = r.updatedAt || r.createdAt;
                  const k = `${r._id || ""}|${r.location || ""}|${r.incidentDate || ""}`;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.location || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.incidentDate || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.incidentTime || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.projectSiteManagersName || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button type="button" onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} /></button>
                          )}
                          <button type="button" onClick={() => generatePDF(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} /></button>
                          <button
                            type="button"
                            onClick={() => {
                              if (!uploadingKey) {
                                setSharePointUploadRecord(r);
                                setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                              }
                            }}
                            disabled={uploadingKey === k}
                            title={uploadingKey === k ? "Uploading..." : "Upload to SharePoint"}
                            style={{ background: "transparent", border: "none", cursor: uploadingKey === k ? "not-allowed" : "pointer", padding: 6, opacity: uploadingKey === k ? 0.6 : 1 }}
                          >
                            <i className={uploadingKey === k ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                          </button>
                          {canEditDelete && (
                            <button type="button" onClick={() => handleDelete(r._id || r.id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME }}>Prev</button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>{page} / {totalPages}</span>
          <button type="button" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME }}>Next</button>
        </div>
      </div>

      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ maxWidth: "600px", margin: "0 auto" }}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType("phase");
                    setSharePointPhaseNumber("15");
                    setSharePointNetwork("");
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{ color: "red" }}>*</span></label>
                    <select
                      className="form-select"
                      value={sharePointPhaseType}
                      onChange={(e) => {
                        setSharePointPhaseType(e.target.value);
                        if (e.target.value === "non-phase") setSharePointNetwork("");
                      }}
                    >
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>
                  {sharePointPhaseType === "phase" && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{ color: "red" }}>*</span></label>
                        <select className="form-select" value={sharePointPhaseNumber} onChange={(e) => setSharePointPhaseNumber(e.target.value)}>
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{ color: "red" }}>*</span></label>
                        <select className="form-select" value={sharePointNetwork} onChange={(e) => setSharePointNetwork(e.target.value)}>
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType("phase");
                    setSharePointPhaseNumber("15");
                    setSharePointNetwork("");
                  }}
                >
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: "#8b1634", color: "#fff", fontWeight: 600 }} onClick={handleSharePointUpload}>
                  Upload to SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 1200, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit SF-003-C Incident Report</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", background: "#f8fafc", padding: 20, color: "#111827" }}>

                <table style={tableStyle}>
                  <tbody>
                    <tr>
                      <td style={headerCell}>Location</td>
                      <td style={headerCell}>Date</td>
                      <td style={headerCell}>Time</td>
                      <td style={headerCell}>Project / Site Managers Name</td>
                      <td style={headerCell}>Supervisors Name</td>
                    </tr>
                    <tr>
                      <td style={bodyCell}><input className="form-control form-control-sm" style={inputStyle} value={editForm.location || ""} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} /></td>
                      <td style={bodyCell}><DatePicker selected={parseStoredDate(editForm.incidentDate)} onChange={(d) => setEditForm({ ...editForm, incidentDate: d ? dateToYmd(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" placeholderText="Select date" customInput={<input className="form-control form-control-sm" style={dateInputStyle} />} /></td>
                      <td style={bodyCell}><input className="form-control form-control-sm" style={inputStyle} value={editForm.incidentTime || ""} onChange={(e) => setEditForm({ ...editForm, incidentTime: e.target.value })} /></td>
                      <td style={bodyCell}><input className="form-control form-control-sm" style={inputStyle} value={editForm.projectSiteManagersName || ""} onChange={(e) => setEditForm({ ...editForm, projectSiteManagersName: e.target.value })} /></td>
                      <td style={bodyCell}><input className="form-control form-control-sm" style={inputStyle} value={editForm.supervisorsName || ""} onChange={(e) => setEditForm({ ...editForm, supervisorsName: e.target.value })} /></td>
                    </tr>
                    <tr>
                      <td style={labelCell}>Was anyone injured?</td>
                      <td style={bodyCell} colSpan={4}>
                        <div style={radioWrap}>
                          <label><input style={themedRadio} type="radio" name="wasAnyoneInjured" checked={editForm.wasAnyoneInjured === "Yes"} onChange={() => setEditForm({ ...editForm, wasAnyoneInjured: "Yes" })} /> Yes</label>
                          <label><input style={themedRadio} type="radio" name="wasAnyoneInjured" checked={editForm.wasAnyoneInjured === "No"} onChange={() => setEditForm({ ...editForm, wasAnyoneInjured: "No" })} /> No</label>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td style={labelCell}>Weather conditions</td>
                      <td style={bodyCell}><div style={radioWrap}><label><input style={themedRadio} type="radio" name="weatherConditions" checked={editForm.weatherConditions === "Fine"} onChange={() => setEditForm({ ...editForm, weatherConditions: "Fine" })} /> Fine</label></div></td>
                      <td style={bodyCell}><div style={radioWrap}><label><input style={themedRadio} type="radio" name="weatherConditions" checked={editForm.weatherConditions === "Raining"} onChange={() => setEditForm({ ...editForm, weatherConditions: "Raining" })} /> Raining</label></div></td>
                      <td style={bodyCell}><div style={radioWrap}><label><input style={themedRadio} type="radio" name="weatherConditions" checked={editForm.weatherConditions === "Snow/Ice"} onChange={() => setEditForm({ ...editForm, weatherConditions: "Snow/Ice" })} /> Snow/Ice</label></div></td>
                      <td style={bodyCell}><div style={radioWrap}><label><input style={themedRadio} type="radio" name="weatherConditions" checked={editForm.weatherConditions === "Windy"} onChange={() => setEditForm({ ...editForm, weatherConditions: "Windy" })} /> Windy</label></div></td>
                    </tr>
                    <tr>
                      <td style={labelCell}>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                          <input style={themedRadio} type="radio" name="weatherConditions" checked={editForm.weatherConditions === "Other (describe)"} onChange={() => setEditForm({ ...editForm, weatherConditions: "Other (describe)" })} />
                          Other (describe)
                        </label>
                      </td>
                      <td style={bodyCell} colSpan={4}>
                        {editForm.weatherConditions === "Other (describe)" ? (
                          <input className="form-control form-control-sm" style={inputStyle} value={editForm.weatherOtherDescribe || ""} onChange={(e) => setEditForm({ ...editForm, weatherOtherDescribe: e.target.value })} />
                        ) : null}
                      </td>
                    </tr>
                    <tr>
                      <td style={labelCell}>Lighting Conditions</td>
                      <td style={bodyCell}><div style={radioWrap}><label><input style={themedRadio} type="radio" name="lightingConditions" checked={editForm.lightingConditions === "Sunny"} onChange={() => setEditForm({ ...editForm, lightingConditions: "Sunny" })} /> Sunny</label></div></td>
                      <td style={bodyCell}><div style={radioWrap}><label><input style={themedRadio} type="radio" name="lightingConditions" checked={editForm.lightingConditions === "Cloudy"} onChange={() => setEditForm({ ...editForm, lightingConditions: "Cloudy" })} /> Cloudy</label></div></td>
                      <td style={bodyCell}><div style={radioWrap}><label><input style={themedRadio} type="radio" name="lightingConditions" checked={editForm.lightingConditions === "Dark"} onChange={() => setEditForm({ ...editForm, lightingConditions: "Dark" })} /> Dark</label></div></td>
                      <td style={bodyCell}><div style={radioWrap}><label><input style={themedRadio} type="radio" name="lightingConditions" checked={editForm.lightingConditions === "Indoors"} onChange={() => setEditForm({ ...editForm, lightingConditions: "Indoors" })} /> Indoors</label></div></td>
                    </tr>
                    <tr>
                      <td style={labelCell}>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                          <input style={themedRadio} type="radio" name="lightingConditions" checked={editForm.lightingConditions === "Other (describe)"} onChange={() => setEditForm({ ...editForm, lightingConditions: "Other (describe)" })} />
                          Other (describe)
                        </label>
                      </td>
                      <td style={bodyCell} colSpan={4}>
                        {editForm.lightingConditions === "Other (describe)" ? (
                          <input className="form-control form-control-sm" style={inputStyle} value={editForm.lightingOtherDescribe || ""} onChange={(e) => setEditForm({ ...editForm, lightingOtherDescribe: e.target.value })} />
                        ) : null}
                      </td>
                    </tr>
                    <tr>
                      <td style={labelCell}>State PPE worn at time of Incident</td>
                      <td style={bodyCell} colSpan={4}><textarea rows={2} className="form-control form-control-sm" style={inputStyle} value={editForm.statePpeWornAtTimeOfIncident || ""} onChange={(e) => setEditForm({ ...editForm, statePpeWornAtTimeOfIncident: e.target.value })} /></td>
                    </tr>
                    <tr>
                      <td style={labelCell}>Fatigue/FRI assessment</td>
                      <td style={bodyCell} colSpan={4}><textarea rows={2} className="form-control form-control-sm" style={inputStyle} value={editForm.fatigueFriAssessment || ""} onChange={(e) => setEditForm({ ...editForm, fatigueFriAssessment: e.target.value })} /></td>
                    </tr>
                    <tr>
                      <td style={labelCell}>Photos taken</td>
                      <td style={bodyCell} colSpan={4}>
                        <div style={radioWrap}>
                          <label><input style={themedRadio} type="radio" name="photosTakenSf003cEdit" checked={editForm.photosTaken === "Yes"} onChange={() => setEditForm({ ...editForm, photosTaken: "Yes" })} /> Yes</label>
                          <label><input style={themedRadio} type="radio" name="photosTakenSf003cEdit" checked={editForm.photosTaken === "No"} onChange={() => setEditForm({ ...editForm, photosTaken: "No" })} /> No</label>
                        </div>
                      </td>
                    </tr>
                    {editForm.photosTaken === "Yes" ? (
                      <tr>
                        <td style={labelCell}>Incident photos</td>
                        <td style={bodyCell} colSpan={4}>
                          <input
                            key={incidentPhotoInputKey}
                            type="file"
                            accept="image/*"
                            multiple
                            className="form-control form-control-sm"
                            style={{ ...inputStyle, marginBottom: 8 }}
                            onChange={handleIncidentPhotosChange}
                          />
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {normalizeIncidentPhotos(editForm.incidentPhotos).map((src, idx) => (
                              <div
                                key={`${idx}-${String(src).slice(0, 24)}`}
                                style={{
                                  position: "relative",
                                  width: 96,
                                  height: 96,
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 8,
                                  overflow: "hidden",
                                  background: "#f9fafb"
                                }}
                              >
                                <img
                                  alt=""
                                  src={src.startsWith("data:") || src.startsWith("http") ? src : `data:image/jpeg;base64,${src}`}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeIncidentPhotoAt(idx)}
                                  style={{
                                    position: "absolute",
                                    top: 2,
                                    right: 2,
                                    border: "none",
                                    borderRadius: 4,
                                    background: "rgba(0,0,0,0.55)",
                                    color: "#fff",
                                    fontSize: 11,
                                    padding: "2px 6px",
                                    cursor: "pointer"
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                    <tr>
                      <td style={labelCell}>Has 'For Cause' D&A testing been initiated?</td>
                      <td style={bodyCell} colSpan={4}>
                        <div style={radioWrap}>
                          <label><input style={themedRadio} type="radio" name="forCauseDaTestingInitiated" checked={editForm.forCauseDaTestingInitiated === "Yes"} onChange={() => setEditForm({ ...editForm, forCauseDaTestingInitiated: "Yes" })} /> Yes</label>
                          <label><input style={themedRadio} type="radio" name="forCauseDaTestingInitiated" checked={editForm.forCauseDaTestingInitiated === "No"} onChange={() => setEditForm({ ...editForm, forCauseDaTestingInitiated: "No" })} /> No</label>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <table style={tableStyle}>
                  <tbody>
                    <tr>
                      <td style={headerCell} colSpan={3}>Notification</td>
                      <td style={headerCell} colSpan={2}>Time / Date</td>
                    </tr>
                    <tr>
                      <td style={plainCell} colSpan={3}>Client</td>
                      <td style={bodyCell} colSpan={2}><DatePicker selected={parseStoredDate(editForm.notification?.clientTimeDate)} onChange={(d) => setEditForm({ ...editForm, notification: { ...(editForm.notification || {}), clientTimeDate: d ? dateToDmyHm(d) : "" } })} showTimeSelect timeIntervals={5} dateFormat="dd/MM/yyyy HH:mm" className="form-control form-control-sm" placeholderText="Select date & time" customInput={<input className="form-control form-control-sm" style={dateInputStyle} />} /></td>
                    </tr>
                    <tr>
                      <td style={plainCell} colSpan={3}>TTSL Project / Line Manager</td>
                      <td style={bodyCell} colSpan={2}><DatePicker selected={parseStoredDate(editForm.notification?.projectLineManagerTimeDate)} onChange={(d) => setEditForm({ ...editForm, notification: { ...(editForm.notification || {}), projectLineManagerTimeDate: d ? dateToDmyHm(d) : "" } })} showTimeSelect timeIntervals={5} dateFormat="dd/MM/yyyy HH:mm" className="form-control form-control-sm" placeholderText="Select date & time" customInput={<input className="form-control form-control-sm" style={dateInputStyle} />} /></td>
                    </tr>
                    <tr>
                      <td style={plainCell} colSpan={3}>TTSL HSQE Manager</td>
                      <td style={bodyCell} colSpan={2}><DatePicker selected={parseStoredDate(editForm.notification?.hsqeManagerTimeDate)} onChange={(d) => setEditForm({ ...editForm, notification: { ...(editForm.notification || {}), hsqeManagerTimeDate: d ? dateToDmyHm(d) : "" } })} showTimeSelect timeIntervals={5} dateFormat="dd/MM/yyyy HH:mm" className="form-control form-control-sm" placeholderText="Select date & time" customInput={<input className="form-control form-control-sm" style={dateInputStyle} />} /></td>
                    </tr>
                    <tr>
                      <td style={headerCell} colSpan={5}>Environmental Details (if applicable)</td>
                    </tr>
                    <tr>
                      <td style={plainCell} colSpan={3}>Spillage / Leakage*</td>
                      <td style={bodyCell} colSpan={2}><input className="form-control form-control-sm" style={inputStyle} value={editForm.environmentalSpillageLeakage || ""} onChange={(e) => setEditForm({ ...editForm, environmentalSpillageLeakage: e.target.value })} /></td>
                    </tr>
                    <tr>
                      <td style={plainCell} colSpan={3}>Material / Substance (Waste, oil, petrol, other chemical)</td>
                      <td style={bodyCell} colSpan={2}><input className="form-control form-control-sm" style={inputStyle} value={editForm.environmentalMaterialSubstance || ""} onChange={(e) => setEditForm({ ...editForm, environmentalMaterialSubstance: e.target.value })} /></td>
                    </tr>
                    <tr>
                      <td style={plainCell} colSpan={3}>Quantity (litres)</td>
                      <td style={bodyCell} colSpan={2}><input className="form-control form-control-sm" style={inputStyle} value={editForm.environmentalQuantityLitres || ""} onChange={(e) => setEditForm({ ...editForm, environmentalQuantityLitres: e.target.value })} /></td>
                    </tr>
                    <tr>
                      <td style={plainCell} colSpan={3}>Other (Noise, Emission to air)</td>
                      <td style={bodyCell} colSpan={2}><input className="form-control form-control-sm" style={inputStyle} value={editForm.environmentalOtherNoiseEmission || ""} onChange={(e) => setEditForm({ ...editForm, environmentalOtherNoiseEmission: e.target.value })} /></td>
                    </tr>
                    <tr>
                      <td style={plainCell} colSpan={3}>Location (ground, drain, watercourse)</td>
                      <td style={bodyCell} colSpan={2}><input className="form-control form-control-sm" style={inputStyle} value={editForm.environmentalLocation || ""} onChange={(e) => setEditForm({ ...editForm, environmentalLocation: e.target.value })} /></td>
                    </tr>
                    <tr>
                      <td style={plainCell} colSpan={3}>Affected Protected Site(s)*</td>
                      <td style={bodyCell} colSpan={2}>
                        <div style={radioWrap}>
                          <label><input style={themedRadio} type="radio" name="affectedProtectedSites" checked={editForm.affectedProtectedSites === "Yes"} onChange={() => setEditForm({ ...editForm, affectedProtectedSites: "Yes" })} /> Yes</label>
                          <label><input style={themedRadio} type="radio" name="affectedProtectedSites" checked={editForm.affectedProtectedSites === "No"} onChange={() => setEditForm({ ...editForm, affectedProtectedSites: "No" })} /> No</label>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td style={plainCell} colSpan={3}>Affected Protected Species*</td>
                      <td style={bodyCell} colSpan={2}>
                        <div style={radioWrap}>
                          <label><input style={themedRadio} type="radio" name="affectedProtectedSpecies" checked={editForm.affectedProtectedSpecies === "Yes"} onChange={() => setEditForm({ ...editForm, affectedProtectedSpecies: "Yes" })} /> Yes</label>
                          <label><input style={themedRadio} type="radio" name="affectedProtectedSpecies" checked={editForm.affectedProtectedSpecies === "No"} onChange={() => setEditForm({ ...editForm, affectedProtectedSpecies: "No" })} /> No</label>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <table style={tableStyle}>
                  <tbody>
                    <tr><td style={headerCell} colSpan={5}>Description of the incident (give a detailed description of the incident using these headings for guidance)</td></tr>
                    <tr><td style={labelCell}>Description</td><td style={labelCell} colSpan={4}>Describe what happened</td></tr>
                    <tr><td style={bodyCell} colSpan={5}><textarea rows={3} className="form-control form-control-sm" style={inputStyle} value={editForm.descriptionWhatHappened || ""} onChange={(e) => setEditForm({ ...editForm, descriptionWhatHappened: e.target.value })} /></td></tr>
                    <tr><td style={labelCell}>Causes</td><td style={labelCell} colSpan={4}>Immediate cause (What would have prevented the incident happening?)</td></tr>
                    <tr><td style={bodyCell} colSpan={5}><textarea rows={3} className="form-control form-control-sm" style={inputStyle} value={editForm.immediateCause || ""} onChange={(e) => setEditForm({ ...editForm, immediateCause: e.target.value })} /></td></tr>
                    <tr><td style={labelCell}>Reporting</td><td style={labelCell} colSpan={4}>Details of how the incident was managed and reported</td></tr>
                    <tr><td style={bodyCell} colSpan={5}><textarea rows={3} className="form-control form-control-sm" style={inputStyle} value={editForm.detailsManagedAndReported || ""} onChange={(e) => setEditForm({ ...editForm, detailsManagedAndReported: e.target.value })} /></td></tr>
                    <tr><td style={labelCell}>Action</td><td style={labelCell} colSpan={4}>Remedial actions (What has been done to prevent re-occurence?)</td></tr>
                    <tr><td style={bodyCell} colSpan={5}><textarea rows={3} className="form-control form-control-sm" style={inputStyle} value={editForm.remedialActions || ""} onChange={(e) => setEditForm({ ...editForm, remedialActions: e.target.value })} /></td></tr>
                  </tbody>
                </table>

                <table style={tableStyle}>
                  <tbody>
                    <tr><td style={headerCell} colSpan={8}>Details of injured/affected person</td></tr>
                    <tr><td style={plainCell} colSpan={8}>Person 1</td></tr>
                    <tr>
                      <td style={plainCell}>Surname</td><td style={bodyCell}><input className="form-control form-control-sm" style={inputStyle} value={editForm.detailsOfInjuredAffectedPerson?.surname || ""} onChange={(e) => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), surname: e.target.value } })} /></td>
                      <td style={plainCell}>First name</td><td style={bodyCell}><input className="form-control form-control-sm" style={inputStyle} value={editForm.detailsOfInjuredAffectedPerson?.firstName || ""} onChange={(e) => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), firstName: e.target.value } })} /></td>
                      <td style={plainCell} colSpan={4}>
                        <div style={radioWrap}>
                          <label><input style={themedRadio} type="radio" name="injuredEmploymentType" checked={editForm.detailsOfInjuredAffectedPerson?.employmentType === "TTSL Employee"} onChange={() => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), employmentType: "TTSL Employee" } })} /> *TTSL Employee</label>
                          <label><input style={themedRadio} type="radio" name="injuredEmploymentType" checked={editForm.detailsOfInjuredAffectedPerson?.employmentType === "Subcontractor"} onChange={() => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), employmentType: "Subcontractor" } })} /> *Subcontractor</label>
                          <label><input style={themedRadio} type="radio" name="injuredEmploymentType" checked={editForm.detailsOfInjuredAffectedPerson?.employmentType === "Visitor"} onChange={() => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), employmentType: "Visitor" } })} /> *Visitor</label>
                        </div>
                      </td>
                    </tr>
                    <tr><td style={plainCell} colSpan={3}>Name of Employer if not TTSLL</td><td style={bodyCell} colSpan={5}><input className="form-control form-control-sm" style={inputStyle} value={editForm.detailsOfInjuredAffectedPerson?.employerName || ""} onChange={(e) => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), employerName: e.target.value } })} /></td></tr>
                    <tr><td style={plainCell} colSpan={3}>Manager or supervisor of injured person:</td><td style={bodyCell} colSpan={5}><input className="form-control form-control-sm" style={inputStyle} value={editForm.detailsOfInjuredAffectedPerson?.managerOrSupervisor || ""} onChange={(e) => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), managerOrSupervisor: e.target.value } })} /></td></tr>
                    <tr><td style={plainCell} colSpan={3}>Telephone Number:</td><td style={bodyCell} colSpan={5}><input className="form-control form-control-sm" style={inputStyle} value={editForm.detailsOfInjuredAffectedPerson?.telephoneNumber || ""} onChange={(e) => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), telephoneNumber: e.target.value } })} /></td></tr>
                    <tr><td style={plainCell} colSpan={3}>Project or Contracting Company</td><td style={bodyCell} colSpan={5}><input className="form-control form-control-sm" style={inputStyle} value={editForm.detailsOfInjuredAffectedPerson?.projectOrContractingCompany || ""} onChange={(e) => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), projectOrContractingCompany: e.target.value } })} /></td></tr>
                    <tr><td style={plainCell} colSpan={8}>Person 2</td></tr>
                    <tr>
                      <td style={plainCell}>Surname</td><td style={bodyCell}><input className="form-control form-control-sm" style={inputStyle} value={editForm.detailsOfInjuredAffectedPerson?.person2?.surname || ""} onChange={(e) => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), person2: { ...(editForm.detailsOfInjuredAffectedPerson?.person2 || {}), surname: e.target.value } } })} /></td>
                      <td style={plainCell}>First name</td><td style={bodyCell}><input className="form-control form-control-sm" style={inputStyle} value={editForm.detailsOfInjuredAffectedPerson?.person2?.firstName || ""} onChange={(e) => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), person2: { ...(editForm.detailsOfInjuredAffectedPerson?.person2 || {}), firstName: e.target.value } } })} /></td>
                      <td style={plainCell} colSpan={4}>
                        <div style={radioWrap}>
                          <label><input style={themedRadio} type="radio" name="injured2EmploymentType" checked={editForm.detailsOfInjuredAffectedPerson?.person2?.employmentType === "TTSL Employee"} onChange={() => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), person2: { ...(editForm.detailsOfInjuredAffectedPerson?.person2 || {}), employmentType: "TTSL Employee" } } })} /> *TTSL Employee</label>
                          <label><input style={themedRadio} type="radio" name="injured2EmploymentType" checked={editForm.detailsOfInjuredAffectedPerson?.person2?.employmentType === "Subcontractor"} onChange={() => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), person2: { ...(editForm.detailsOfInjuredAffectedPerson?.person2 || {}), employmentType: "Subcontractor" } } })} /> *Subcontractor</label>
                          <label><input style={themedRadio} type="radio" name="injured2EmploymentType" checked={editForm.detailsOfInjuredAffectedPerson?.person2?.employmentType === "Visitor"} onChange={() => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), person2: { ...(editForm.detailsOfInjuredAffectedPerson?.person2 || {}), employmentType: "Visitor" } } })} /> *Visitor</label>
                        </div>
                      </td>
                    </tr>
                    <tr><td style={plainCell} colSpan={3}>Name of Employer if not TTSLL</td><td style={bodyCell} colSpan={5}><input className="form-control form-control-sm" style={inputStyle} value={editForm.detailsOfInjuredAffectedPerson?.person2?.employerName || ""} onChange={(e) => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), person2: { ...(editForm.detailsOfInjuredAffectedPerson?.person2 || {}), employerName: e.target.value } } })} /></td></tr>
                    <tr><td style={plainCell} colSpan={3}>Manager or supervisor of injured person:</td><td style={bodyCell} colSpan={5}><input className="form-control form-control-sm" style={inputStyle} value={editForm.detailsOfInjuredAffectedPerson?.person2?.managerOrSupervisor || ""} onChange={(e) => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), person2: { ...(editForm.detailsOfInjuredAffectedPerson?.person2 || {}), managerOrSupervisor: e.target.value } } })} /></td></tr>
                    <tr><td style={plainCell} colSpan={3}>Telephone Number:</td><td style={bodyCell} colSpan={5}><input className="form-control form-control-sm" style={inputStyle} value={editForm.detailsOfInjuredAffectedPerson?.person2?.telephoneNumber || ""} onChange={(e) => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), person2: { ...(editForm.detailsOfInjuredAffectedPerson?.person2 || {}), telephoneNumber: e.target.value } } })} /></td></tr>
                    <tr><td style={plainCell} colSpan={3}>Project or Contracting Company</td><td style={bodyCell} colSpan={5}><input className="form-control form-control-sm" style={inputStyle} value={editForm.detailsOfInjuredAffectedPerson?.person2?.projectOrContractingCompany || ""} onChange={(e) => setEditForm({ ...editForm, detailsOfInjuredAffectedPerson: { ...(editForm.detailsOfInjuredAffectedPerson || {}), person2: { ...(editForm.detailsOfInjuredAffectedPerson?.person2 || {}), projectOrContractingCompany: e.target.value } } })} /></td></tr>
                    <tr><td style={headerCell} colSpan={8}>Details of Injury*</td></tr>
                    <tr>
                      <td style={bodyCell} colSpan={8}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(130px, 1fr))", gap: 8, padding: "8px 10px" }}>
                          {INJURY_TYPE_OPTIONS.map((opt) => (
                            <label key={opt} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <input
                                style={themedRadio}
                                type="checkbox"
                                checked={Array.isArray(editForm.injuryType) && editForm.injuryType.includes(opt)}
                                onChange={(e) => setEditForm({
                                  ...editForm,
                                  injuryType: e.target.checked
                                    ? [...(Array.isArray(editForm.injuryType) ? editForm.injuryType : []), opt]
                                    : (Array.isArray(editForm.injuryType) ? editForm.injuryType : []).filter((x) => x !== opt)
                                })}
                              />
                              {opt === "Other" ? "Other:" : opt}
                            </label>
                          ))}
                        </div>
                        {Array.isArray(editForm.injuryType) && editForm.injuryType.includes("Other") ? (
                          <div style={{ padding: "0 10px 8px" }}>
                            <input className="form-control form-control-sm" style={inputStyle} value={editForm.injuryTypeOther || ""} onChange={(e) => setEditForm({ ...editForm, injuryTypeOther: e.target.value })} />
                          </div>
                        ) : null}
                      </td>
                    </tr>
                    <tr>
                      <td style={plainCell} colSpan={8}>Body type</td>
                    </tr>
                    <tr>
                      <td style={bodyCell} colSpan={8}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(120px, 1fr))", gap: 8, padding: "8px 10px" }}>
                          {BODY_TYPE_OPTIONS.map((opt) => (
                            <label key={opt} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <input
                                style={themedRadio}
                                type="checkbox"
                                checked={Array.isArray(editForm.injuryBodyType) && editForm.injuryBodyType.includes(opt)}
                                onChange={(e) => setEditForm({
                                  ...editForm,
                                  injuryBodyType: e.target.checked
                                    ? [...(Array.isArray(editForm.injuryBodyType) ? editForm.injuryBodyType : []), opt]
                                    : (Array.isArray(editForm.injuryBodyType) ? editForm.injuryBodyType : []).filter((x) => x !== opt)
                                })}
                              />
                              {opt}
                            </label>
                          ))}
                </div>
                      </td>
                    </tr>
                    <tr><td style={plainCell} colSpan={8}>The injured person was *</td></tr>
                    <tr>
                      <td style={bodyCell} colSpan={8}>
                        <div style={radioWrap}>
                          <label><input style={themedRadio} type="radio" name="injuredPersonOutcome" checked={editForm.injuredPersonOutcome === "Returned to work"} onChange={() => setEditForm({ ...editForm, injuredPersonOutcome: "Returned to work" })} /> Returned to work</label>
                          <label><input style={themedRadio} type="radio" name="injuredPersonOutcome" checked={editForm.injuredPersonOutcome === "Sent home"} onChange={() => setEditForm({ ...editForm, injuredPersonOutcome: "Sent home" })} /> Sent home</label>
                          <label><input style={themedRadio} type="radio" name="injuredPersonOutcome" checked={editForm.injuredPersonOutcome === "Sent to hospital"} onChange={() => setEditForm({ ...editForm, injuredPersonOutcome: "Sent to hospital" })} /> Sent to hospital</label>
                        </div>
                      </td>
                    </tr>
                    <tr><td style={headerCell} colSpan={8}>Details of Hospital</td></tr>
                    <tr>
                      <td style={plainCell} colSpan={4}>Name</td>
                      <td style={plainCell} colSpan={4}>Phone Number</td>
                    </tr>
                    <tr>
                      <td style={bodyCell} colSpan={4}><input className="form-control form-control-sm" style={inputStyle} value={editForm.hospitalDetailsName || ""} onChange={(e) => setEditForm({ ...editForm, hospitalDetailsName: e.target.value })} /></td>
                      <td style={bodyCell} colSpan={4}><input className="form-control form-control-sm" style={inputStyle} value={editForm.hospitalDetailsPhone || ""} onChange={(e) => setEditForm({ ...editForm, hospitalDetailsPhone: e.target.value.replace(/[^0-9]/g, "").slice(0, 12) })} maxLength={12} inputMode="numeric" pattern="[0-9]*" /></td>
                    </tr>
                  </tbody>
                </table>

                <table style={tableStyle}>
                  <tbody>
                    <tr>
                      <td style={headerCell}>First name/Surname</td>
                      <td style={headerCell}>Title/Position</td>
                      <td style={headerCell}>Telephone Number</td>
                    </tr>
                    <tr>
                      <td style={bodyCell}><input className="form-control form-control-sm" style={inputStyle} value={editForm.reviewFirstNameSurname || ""} onChange={(e) => setEditForm({ ...editForm, reviewFirstNameSurname: e.target.value })} /></td>
                      <td style={bodyCell}><input className="form-control form-control-sm" style={inputStyle} value={editForm.reviewTitlePosition || ""} onChange={(e) => setEditForm({ ...editForm, reviewTitlePosition: e.target.value })} /></td>
                      <td style={bodyCell}><input className="form-control form-control-sm" style={inputStyle} value={editForm.reviewTelephoneNumber || ""} onChange={(e) => setEditForm({ ...editForm, reviewTelephoneNumber: e.target.value.replace(/[^0-9]/g, "").slice(0, 12) })} maxLength={12} inputMode="numeric" pattern="[0-9]*" /></td>
                    </tr>
                    <tr>
                      <td style={plainCell} colSpan={2}>Has 'For Cause' Drug and Alcohol Testing been requested?*</td>
                      <td style={bodyCell}><div style={radioWrap}><label><input style={themedRadio} type="radio" name="forCauseDrugAlcoholRequested2" checked={editForm.forCauseDrugAlcoholRequested === "Yes"} onChange={() => setEditForm({ ...editForm, forCauseDrugAlcoholRequested: "Yes" })} /> Yes</label><label><input style={themedRadio} type="radio" name="forCauseDrugAlcoholRequested2" checked={editForm.forCauseDrugAlcoholRequested === "No"} onChange={() => setEditForm({ ...editForm, forCauseDrugAlcoholRequested: "No" })} /> No</label></div></td>
                    </tr>
                    <tr>
                      <td style={plainCell} colSpan={2}>Has client been notified?</td>
                      <td style={bodyCell}><div style={radioWrap}><label><input style={themedRadio} type="radio" name="clientNotified" checked={editForm.clientNotified === "Yes"} onChange={() => setEditForm({ ...editForm, clientNotified: "Yes" })} /> Yes</label><label><input style={themedRadio} type="radio" name="clientNotified" checked={editForm.clientNotified === "No"} onChange={() => setEditForm({ ...editForm, clientNotified: "No" })} /> No</label></div></td>
                    </tr>
                    <tr>
                      <td style={labelCell} colSpan={2}>Report completed</td>
                      <td style={plainCell}>Telephone Number:</td>
                    </tr>
                    <tr>
                      <td style={bodyCell} colSpan={2}><input className="form-control form-control-sm" style={inputStyle} value={editForm.reportCompletedName || ""} onChange={(e) => setEditForm({ ...editForm, reportCompletedName: e.target.value })} /></td>
                      <td style={bodyCell}><input className="form-control form-control-sm" style={inputStyle} value={editForm.reportCompletedTelephone || ""} onChange={(e) => setEditForm({ ...editForm, reportCompletedTelephone: e.target.value.replace(/[^0-9]/g, "").slice(0, 12) })} maxLength={12} inputMode="numeric" pattern="[0-9]*" /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={handleEditSave}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-003-C Incident Report File"
        importPath="incident-report-sf003c/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setRecords((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-003-C Incident Report</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this record?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
